import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';

type UploadedFilesSummaryProps = {
  count: number;
  fileNames: string[];
  /** Singular noun, e.g. "policy document", "dataset", "ML model". */
  itemLabel?: string;
  /** When true, shows the pending state instead of the success list. */
  showPendingWhenEmpty?: boolean;
  /** Pending copy when showPendingWhenEmpty is true. */
  emptyMessage?: string;
  /** Override the secondary hint under the file list. */
  hint?: string;
};

/** Shared success/pending summary under upload UIs. */
const UploadedFilesSummary = ({
  count,
  fileNames,
  itemLabel = 'policy document',
  showPendingWhenEmpty = false,
  emptyMessage = 'No policy document uploaded yet.',
  hint = 'You can add more or continue.'
}: UploadedFilesSummaryProps) => {
  if (count <= 0) {
    if (!showPendingWhenEmpty) return null;
    return (
      <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
        {emptyMessage}
      </Typography>
    );
  }

  const noun = count === 1 ? itemLabel : `${itemLabel}s`;

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
        {count} {noun} uploaded.
      </Typography>
      {fileNames.length > 0 ? (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.75, mb: 0.5, p: 0 }}>
          {fileNames.map(name => (
            <Box
              component="li"
              key={name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.35
              }}
            >
              <FontAwesomeIcon icon={faFileAlt} style={{ color: '#3872fa', fontSize: 14, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ wordBreak: 'break-word', color: 'text.primary', lineHeight: 1.35 }}>
                {name}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
      {hint ? (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
};

/** @deprecated Use UploadedFilesSummary */
const UploadedPolicyFilesSummary = UploadedFilesSummary;

export { UploadedFilesSummary };
export default UploadedPolicyFilesSummary;
